import { TranslationsContext } from '@teliaee/telia-translate';
import { action } from 'mobx';
import { inject, observer } from 'mobx-react';
import React from 'react';
import { Card, CardContent, Grid, GridColumn, Select, Separator, TSelectSimpleValue } from 'telia-front-react';
// eslint-disable-next-line import/no-internal-modules
import escape from 'validator/es/lib/escape';
import GaDataStore from '../stores/GaDataStore';
import RouteStore from '../stores/RouteStore';
import SearchStore, {
  CATEGORY_ARCHIVED,
  CONTENT_CATEGORY,
  EMPLOYEE_CATEGORY,
  ESHOP_CATEGORY,
  NEWS_CATEGORY,
  ONLINE_HELP_CATEGORY,
  SELFSERVICE_CATEGORY
} from '../stores/SearchStore';
import SegmentStore from '../stores/SegmentStore';
import { createParamsString } from '../util/PathUtil';
import { getCustomerType } from '../util/SegmentUtil';
import { toCamelCase } from '../util/TextUtil';
import { BASE_SEARCH_GA } from './SearchGaEvents';
import {
  AllAndArchivedFilterOrder,
  DealerNewsFilterOrder,
  DealerNewsSortOrder,
  NewsSortOrder,
  ProductsSortOrder,
  WebContentFilterOrder
} from './SearchInterfaces';

interface SearchResultsSortBarProps {
  category?: string;
  searchStore?: SearchStore;
  routeStore?: RouteStore;
  segmentStore?: SegmentStore;
  isMobile?: boolean;
}

@observer
class SearchResultsSortBar extends React.Component<SearchResultsSortBarProps> {
  static contextType = TranslationsContext;
  declare context: React.ContextType<typeof TranslationsContext>;

  private getCriteriaLabel(sortKey: string, category: string, isSortCriteria?: boolean) {
    if (!sortKey) {
      return undefined;
    }

    const translate = this.context.translate;
    const key = toCamelCase(sortKey.toLowerCase());

    let numberOfResults = 0;

    if ([AllAndArchivedFilterOrder.ALL, AllAndArchivedFilterOrder.ARCHIVED].includes(key as AllAndArchivedFilterOrder) && category) {
      const categoryName = key === AllAndArchivedFilterOrder.ARCHIVED ? `${category}${CATEGORY_ARCHIVED}` : category;
      const isArchived = categoryName.includes(CATEGORY_ARCHIVED);
      numberOfResults = this.props.searchStore.getSearchResultCount(categoryName, isArchived);
    }

    if (
      Object.values(DealerNewsFilterOrder).includes(key as DealerNewsFilterOrder) &&
      key !== DealerNewsFilterOrder.ARCHIVED &&
      category === EMPLOYEE_CATEGORY
    ) {
      numberOfResults = this.props.searchStore.getDealerNewsTopicResultCount(key);
    }

    if ([WebContentFilterOrder.PRIVATE, WebContentFilterOrder.BUSINESS].includes(key as WebContentFilterOrder) && category === CONTENT_CATEGORY) {
      numberOfResults =
        key === WebContentFilterOrder.PRIVATE ? this.props.searchStore.allContentResults.privateTotal : this.props.searchStore.allContentResults.businessTotal;
    }

    const labelCustomCategory = ['shop', 'news'].includes(category) ? `${category}.` : '';
    return isSortCriteria ? translate(`search.${key}`) : `${translate(`search.${labelCustomCategory}${key}`)} (${numberOfResults})`;
  }

  private pushSortBarGaData = (section: 'Sort' | 'Filter', order: string) => {
    GaDataStore.pushWithoutCmsData({
      ...BASE_SEARCH_GA(getCustomerType(this.props.segmentStore.currentSegment)),
      eventLabel: `Tab - ${this.props.category} - ${section} - ${order}`
    });
  };

  @action
  private sortEShopSearchResults(order: ProductsSortOrder) {
    const {
      searchStore,
      routeStore: { pathname, decodedParams, replace }
    } = this.props;
    this.pushSortBarGaData('Sort', order);
    replace(pathname + createParamsString({ ...decodedParams, sort: order.toLowerCase() }));
    searchStore.refetchEShopSearchResults();
  }

  private resolveFilterOptionsForCategory(category: string) {
    switch (category) {
      case CONTENT_CATEGORY:
        return WebContentFilterOrder;
      case EMPLOYEE_CATEGORY:
        return DealerNewsFilterOrder;
      case NEWS_CATEGORY:
      case ESHOP_CATEGORY:
        return AllAndArchivedFilterOrder;
      default:
        return undefined;
    }
  }

  private resolveFilterActionForCategory(category: string, order: any) {
    const { searchStore } = this.props;
    switch (category) {
      case EMPLOYEE_CATEGORY:
        return searchStore.changeDealerNewsFilterOrder(order);
      case NEWS_CATEGORY:
        return searchStore.changeNewsFilterOrder(order);
      case CONTENT_CATEGORY:
        return searchStore.changeWebContentFilters(order);
      case ESHOP_CATEGORY:
        return searchStore.changeShopFilterOrder(order);
      default:
        return undefined;
    }
  }

  private renderFilterOptions(category: string) {
    const categoryFilterOptions = this.resolveFilterOptionsForCategory(category);
    const { searchStore } = this.props;
    const sortKey = category === 'content' ? searchStore.allContentResults.filterOrder : searchStore.searchWrappers[category].filterOrder;

    const handleChange = (selectedValue: TSelectSimpleValue) => {
      if (typeof selectedValue === 'string') {
        this.pushSortBarGaData('Filter', selectedValue);
        this.resolveFilterActionForCategory(category, selectedValue);
      }
    };

    return (
      <GridColumn width={['xs-12', 'min-md']}>
        <Select
          id={`${category}-filter-dropdown`}
          simpleValue={true}
          size="small"
          hideLabel="no-break"
          className="text-small"
          defaultValue={searchStore.allContentResults.filterOrder}
          label={this.getCriteriaLabel(sortKey, category)}
          options={Object.values(categoryFilterOptions).map((order) => ({
            value: order,
            label: this.getCriteriaLabel(order, category)
          }))}
          onChange={handleChange}
        />
      </GridColumn>
    );
  }

  private renderSortOptions(category: string) {
    const categorySortOptions = category === EMPLOYEE_CATEGORY ? DealerNewsSortOrder : NewsSortOrder;
    const { searchStore } = this.props;
    const handleChange = (selectedValue: TSelectSimpleValue) => {
      if (typeof selectedValue === 'string') {
        this.pushSortBarGaData('Sort', selectedValue);
        category === EMPLOYEE_CATEGORY
          ? searchStore.sortDealerNews(selectedValue as DealerNewsSortOrder)
          : searchStore.sortNews(selectedValue as NewsSortOrder);
      }
    };

    return (
      <GridColumn width={['xs-12', 'min-md']}>
        <Select
          id={`${category}-sort-dropdown`}
          simpleValue={true}
          size="small"
          hideLabel="no-break"
          className="text-small"
          defaultValue={searchStore.searchWrappers[category].sortOrder}
          label={this.getCriteriaLabel(searchStore.searchWrappers[category].sortOrder, category, true)}
          onChange={handleChange}
          options={Object.values(categorySortOptions).map((order) => ({
            value: order,
            label: this.getCriteriaLabel(order, category, true)
          }))}
        />
      </GridColumn>
    );
  }

  private renderShopSortOptions() {
    const { searchStore } = this.props;
    const handleChange = (selectedValue: TSelectSimpleValue) => {
      if (typeof selectedValue === 'string') {
        this.sortEShopSearchResults(selectedValue as ProductsSortOrder);
      }
    };

    return (
      <GridColumn width={['xs-12', 'min-md']}>
        <Select
          id="shop-searchresults-sortbar-dropdown"
          label={this.getCriteriaLabel(searchStore.getEShopProductSortOrder(), ESHOP_CATEGORY, true)}
          simpleValue={true}
          size="small"
          hideLabel="no-break"
          className="text-small"
          defaultValue={searchStore.getEShopProductSortOrder()}
          onChange={handleChange}
          options={Object.values(ProductsSortOrder).map((order) => ({
            value: order,
            label: this.getCriteriaLabel(order, ESHOP_CATEGORY, true)
          }))}
        />
      </GridColumn>
    );
  }

  public render() {
    const translate = this.context.translate;
    const { searchStore, category, isMobile } = this.props;

    if ([ONLINE_HELP_CATEGORY, SELFSERVICE_CATEGORY].includes(category)) {
      return null;
    }

    const numberOfResultsInCategory = searchStore.getSearchResultCount(category, true);
    return (
      <>
        <Card padding="small">
          <CardContent>
            <Grid align={['between-sm']}>
              <GridColumn width={['xs-12', 'min-md']} className="text-small text-light">
                <p>
                  <span className="sr-only">
                    {translate('search.resultsSummary.category', {
                      category: translate(`search.${category}`)
                    })}
                  </span>
                  <span>{translate('search.resultsSummary', { numberOfResults: numberOfResultsInCategory, searchQuery: escape(searchStore.query) })}</span>
                </p>
              </GridColumn>
              {isMobile && (
                <GridColumn>
                  <Separator spacing="xxsmall" weight="thin" />
                </GridColumn>
              )}
              <GridColumn width={['xs-12', 'min-md']}>
                <Grid align={['between-sm']}>
                  {this.renderFilterOptions(category)}
                  {category === ESHOP_CATEGORY && this.renderShopSortOptions()}
                  {[NEWS_CATEGORY, EMPLOYEE_CATEGORY].includes(category) && this.renderSortOptions(category)}
                </Grid>
              </GridColumn>
            </Grid>
          </CardContent>
        </Card>
        <Separator color="invisible" spacing="small" />
      </>
    );
  }
}

export default inject('searchStore', 'routeStore', 'segmentStore')(SearchResultsSortBar);
